import React from "react";
import { Helmet } from "react-helmet";
import { ShareSection } from "../../components/template";

// Hashtag section
const HashtagSection = ({ tags }) => (
  <div className="mt-6 flex flex-wrap md:gap-3 gap-1 justify-center md:mb-6 mb-3">
    {tags.map((tag, index) => (
      <span
        key={index}
        className="bg-red-700 bg-opacity-10 text-red-700 px-4 py-2 rounded-full text-sm font-semibold cursor-pointer transition-all duration-300 m-1 mb-2 hover:bg-red-700 hover:text-white"
      >
        {tag}
      </span>
    ))}
  </div>
);

const Blog14= () => {
  const hashtags = [
    "#bodywisdom",
    "#mindbodyconnection",
    "#selfawareness",
    "#healingjourney",
    "#ParamLife",
  ];

  return (
    <>
      <Helmet>
        <title>
          9 Hidden Signs Your Body Needs Attention (Even If You Think It’s
          Functioning Normal)
        </title>
      </Helmet>

      <article className="max-w-4xl mx-auto mt-10 px-6 py-4 leading-relaxed text-gray-800">
        <h1 className="text-secondary text-left ml-10 text-3xl font-semibold mb-8">
          9 Hidden Signs Your Body Needs Attention
          <br />
          (Even If You Think It’s Functioning Normal)
        </h1>
   <img
          src="/assets/knowyourself/blogs/blog14.png"
          alt="7 Things Our Grandparents Did Right (But We Ruined)"
          className="w-[80%] max-w-xl rounded-2xl shadow-md mx-auto mb-10 ml-10"
        />

        <section className="space-y-4 mb-8">
          <h2 className="text-secondary text-2xl font-semibold">
            You’re Functioning, But Are You Really Okay?
          </h2>

          <p>
            It’s funny — in today’s world, “I’m fine” has become our favorite
            mask.
          </p>

          <p>
            We wake up tired, pour ourselves a cup of coffee, scroll through the
            noise, and get on with the day.
          </p>

          <p>We call that functioning.</p>

          <p className="text-[#578d48] font-semibold">
            But functioning isn’t the same as thriving.
          </p>

          <p>
            You can appear okay — smiling, productive, “on top of things” — and
            still be quietly falling apart inside.
          </p>

          <p className="text-[#578d48] font-semibold">
            Your body has a voice.
          </p>

          <p>
            It whispers first — through little signs, sensations, and patterns.
          </p>

          <p>
            If you ignore those whispers long enough, they turn into shouts.
          </p>

          <p>
            This post isn’t about health in a clinical sense.
          </p>

          <p>
            It’s about awareness — the kind that comes when you learn to listen
            to what your body has been trying to tell you all along.
          </p>

          <p className="text-[#578d48] font-semibold">
            Here are 9 hidden signs your body might be needing your attention —
            even if everything “seems fine” on the surface.
          </p>
        </section>

        <section className="space-y-6 mb-8">
          <div>
            <h3 className="text-primary font-semibold">
              1. You’re always tired, even after a full night’s sleep
            </h3>
            <p>
              You tell yourself that you simply need to sleep better. What if
              it's more than simply physical, though?
            </p>

            <p>
              Fatigue is frequently caused by mental and physical strain rather
              than a lack of sleep. Your mind remains active even when your eyes
              are closed when you have too many unresolved issues,
              unacknowledged emotions, and never-ending to-do lists.
            </p>

            <p className="text-[#578d48]">
              Easing your soul might heal you more than resting your body.
            </p>

            <p>
              Before going to bed, try writing down your ideas or just
              whispering, "I choose peace for tonight," to help you relax.
            </p>

            <p>
              Your body can’t recharge when your mind is still running
              marathons.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              2. You’re craving sugar, salt, or caffeine constantly
            </h3>

            <p className="text-[#578d48]">
              Your cravings are messengers — not enemies.
            </p>

            <p>Sugar cravings can signal emotional emptiness.</p>
            <p>Salt cravings can hint at stress or dehydration.</p>
            <p>
              Caffeine dependence might be your body begging for natural energy —
              rest, sunlight, grounding.
            </p>

            <p>
              Instead of fighting the craving, ask what it’s trying to soothe.
            </p>

            <p className="text-[#578d48]">
              Maybe you need comfort, not cookies.
              <br />
              Maybe you need rest, not another cup of coffee.
            </p>

            <p>
              When you listen with kindness, even your cravings become teachers.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              3. You feel bloated or heavy after meals
            </h3>

            <p>
              You might think it’s just digestion — but it’s often more than
              that.
            </p>

            <p className="text-[#578d48]">
              Our gut reacts to more than food. It reacts to emotion.
            </p>

            <p>
              When you eat in a rush, under stress, or while scrolling through
              bad news — your body can’t relax enough to digest properly.
            </p>

            <p>Slow down.</p>
            <p>Breathe between bites.</p>
            <p>Bless your food before you eat.</p>

            <p className="text-[#578d48]">
              When you eat with presence, your body responds with gratitude.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              4. You get frequent headaches or neck tension
            </h3>

            <p className="text-[#578d48]">
              Headaches are often the body’s way of saying, “You’re thinking too
              much.”
            </p>

            <p>
              Tension builds up not just from poor posture — but from emotional
              weight carried in your shoulders and jaw.
            </p>

            <p>Next time you feel that dull ache, pause.</p>
            <p>Close your eyes.</p>
            <p>Ask yourself, “What am I trying to control right now?”</p>

            <p className="text-[#578d48]">
              Because sometimes, the cure for tension isn’t a pill — it’s
              permission to let go.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              5. You’ve stopped feeling joy in the little things
            </h3>

            <p>
              Maybe you used to love your morning walk, your favorite music, or
              that first sip of coffee.
            </p>

            <p>But lately, it all feels… dull.</p>

            <p className="text-[#578d48]">
              That’s not laziness. That’s burnout.
            </p>

            <p>
              When your nervous system is stuck in “survival mode,” joy becomes
              harder to feel — even when life is okay.
            </p>

            <p>
              Your body needs moments of safety and softness to reawaken your
              sense of pleasure.
            </p>

            <p className="text-[#578d48]">
              Joy returns not when life becomes easier — but when your heart
              feels safe again.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              6. You’re getting sick more often
            </h3>

            <p className="text-[#578d48]">
              Your immune system listens to your emotions more than you realize.
            </p>

            <p>
              Chronic stress, unprocessed sadness, and lack of rest all whisper
              to your immune cells: “We’re not safe.”
            </p>

            <p>
              If you’re catching colds often or feeling run down, it might be
              your body’s way of asking for slowness.
            </p>

            <p>Warm food. Deep breaths. Early nights.</p>

            <p>
              And a promise to stop glorifying exhaustion as productivity.
            </p>

            <p className="text-[#578d48]">
              Your body is your home — not your battlefield.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              7. You’re forgetting small things lately
            </h3>

            <p>
              That foggy feeling? That “Where did I keep my keys?” moment?
            </p>

            <p>
              It’s not always a lack of focus — it’s a sign of overwhelm.
            </p>

            <p>
              When your mind is overloaded, your memory becomes the first
              casualty.
            </p>

            <p>Your brain can’t organize thoughts when it’s constantly firefighting.</p>

            <p>Try taking mini breaks:</p>
            <p>Stare at the sky for two minutes.</p>
            <p>Listen to music without doing anything else.</p>
            <p>Breathe deeply, slowly, and intentionally.</p>

            <p className="text-[#578d48]">
              Your mind doesn’t need more information. It needs space.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              8. You feel disconnected from your body
            </h3>

            <p>
              Ever catch yourself feeling like your mind and body are two
              separate things?
            </p>

            <p>That’s disconnection — a modern illness.</p>

            <p>
              It happens when we spend too much time in our heads and not enough
              time feeling.
            </p>

            <p>
              We think our way through life instead of experiencing it.
            </p>

            <p>Reconnecting doesn’t require fancy practices.</p>
            <p>Try this:</p>

            <p>Touch your heart gently.</p>
            <p>Take one deep breath.</p>
            <p>Say to yourself, “I’m here. I’m listening.”</p>

            <p className="text-[#578d48]">
              That’s presence.
              <br />
              That’s peace.
              <br />
              That’s the bridge back to yourself.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              9. You sense something’s off, but can’t explain it
            </h3>

            <p className="text-[#578d48]">Intuition is real.</p>

            <p>
              Sometimes, your body knows something your mind doesn’t yet
              understand.
            </p>

            <p>
              That unease in your stomach? That tightness in your chest?
            </p>

            <p>
              It might not be “anxiety” — it might be wisdom.
            </p>

            <p>Don’t rush to silence it with distraction.</p>
            <p>Sit with it. Ask it what it needs.</p>

            <p className="text-[#578d48]">
              You’ll be surprised how clearly it answers when you finally stop
              to listen.
            </p>
          </div>
        </section>

        <section className="space-y-4 mb-10">
          <h2 className="text-secondary text-2xl font-semibold">
            Your Body Isn’t Broken — It’s Just Asking for You
          </h2>

          <p>
            We live in a culture that teaches us to push through.
          </p>

          <p>
            To ignore the pain. To “be strong.” To keep going no matter what.
          </p>

          <p className="text-[#578d48]">
            But true strength isn’t about pushing.
            <br />
            It’s about pausing.
          </p>

          <p className="text-[#578d48]">
            Your body doesn’t need punishment — it needs partnership.
          </p>

          <p className="text-[#578d48]">
            It doesn’t want you to hustle harder — it wants you to come home.
          </p>

          <p className="text-[#578d48] font-semibold">
            You are not lazy. You are not weak. You are not behind.
          </p>

          <p className="text-[#578d48]">
            You’re being invited gently into alignment.
            <br />
            To slow down, breathe deeply, and honor the sacred dialogue between
            your body, mind, and spirit. Because every time you listen, a part
            of you returns home.
          </p>
        </section>

        <HashtagSection tags={hashtags} />
        <ShareSection />
      </article>
    </>
  );
};

export default Blog14;
